import React from "react";
import type { CampaignReportResponse } from "../../types";
import type { ExportTemplateModule } from "../types";
import {
  coerceNumber,
  formatCurrency,
  getTopBrands,
  parseCalloutsText,
} from "../utils";

interface KeyCalloutsModuleProps {
  data: CampaignReportResponse;
  module: ExportTemplateModule<"callouts">;
  callouts?: string;
}

export const KeyCalloutsModule: React.FC<KeyCalloutsModuleProps> = ({
  data,
  module,
  callouts,
}) => {
  const calloutItems = parseCalloutsText(callouts);

  const fallback =
    calloutItems.length > 0
      ? calloutItems
      : buildFallbackCallouts(data);

  return (
    <section>
      {module.title && (
        <div className="text-slate-400 uppercase tracking-[0.2em] text-xs font-semibold mb-3">
          {module.title}
        </div>
      )}
      <div className="rounded-[24px] bg-white border border-slate-100 shadow-[0_25px_60px_rgba(15,23,42,0.07)] p-6 grid grid-cols-3 gap-4 text-slate-900">
        {fallback.map((item, index) => (
          <div
            key={`${item}-${index}`}
            className="rounded-2xl p-5 h-full flex flex-col gap-3 shadow-inner"
            style={{
              background:
                index === 0
                  ? "linear-gradient(135deg,#fff7f2,#ffeef6)"
                : index === 1
                    ? "linear-gradient(135deg,#f0f9ff,#e1f2ff)"
                    : "linear-gradient(135deg,#fffbea,#fff4d4)",
            }}
          >
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500/80">
              {`Callout ${index + 1}`}
            </div>
            <p className="text-base font-semibold leading-snug text-slate-900">
              {item}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

function buildFallbackCallouts(data: CampaignReportResponse): string[] {
  const topBrands = getTopBrands(data.brands ?? [], "total_sales", 3);
  const bestBrand = topBrands[0];
  const bestRoas =
    data.brands?.reduce((prev, current) => {
      if (!prev) return current;
      return coerceNumber(current.roas) > coerceNumber(prev.roas)
        ? current
        : prev;
    }, data.brands?.[0]) ?? null;

  const callouts: string[] = [];

  if (bestBrand) {
    callouts.push(
      `${bestBrand.brand} leads sales with ${formatCurrency(
        bestBrand.total_sales,
      )} across ${bestBrand.campaign_count} campaigns.`,
    );
  }

  if (bestRoas) {
    callouts.push(
      `${bestRoas.brand} delivered the strongest ROAS at ${Number.parseFloat(
        String(bestRoas.roas),
      ).toFixed(2)}x with ${formatCurrency(bestRoas.total_spend)} spend.`,
    );
  }

  callouts.push(
    `Overall media drove ${formatCurrency(
      data.summary.total_sales,
    )} in sales vs. ${formatCurrency(data.summary.total_spend)} spend.`,
  );

  return callouts.slice(0, 3);
}
